/**
 * SEO Component
 * 
 * Handles page meta tags for SEO optimization using react-helmet-async.
 * 
 * @component
 * @example
 * ```tsx
 * <SEO 
 *   title="About Us"
 *   description="Learn about RaveRemedy's mission and values"
 * />
 * ```
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  image?: string;
  keywords?: string;
  canonical?: string;
  type?: string;
  noindex?: boolean;
}

export const SEO: React.FC<SEOProps> = ({ 
  title, 
  description,
  image = 'https://raveremedy.com/01.png',
  keywords,
  canonical,
  type = 'website',
  noindex = false
}) => {
  const fullTitle = `${title} | RaveRemedy`;
  const currentUrl = typeof window !== 'undefined' ? window.location.href : 'https://raveremedy.com';
  const canonicalUrl = canonical || currentUrl;
  
  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'} />

      {/* Open Graph meta tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={`${title} - RaveRemedy`} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="RaveRemedy" />
      <meta property="og:locale" content="en_AU" />

      {/* Twitter meta tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@raveremedyau" />
      <meta name="twitter:creator" content="@raveremedyau" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={`${title} - RaveRemedy`} />
    </Helmet>
  );
};